import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { supabase, generateServiceLink } from "@/integrations/supabase/client";
import { generateQRCodeUrl, createStyledQRCode } from "@/utils/qrCode";

// Define profile interface to match the fields we're querying
interface Profile {
  service_page_link?: string;
  company_name?: string;
  profile_bio?: string;
  profile_image_url?: string;
  banner_image_url?: string;
}

export const useServiceShare = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [serviceLink, setServiceLink] = useState("");
  const [businessName, setBusinessName] = useState("My Business");
  const [profileBio, setProfileBio] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [bannerImageUrl, setBannerImageUrl] = useState("");
  const [isSavingLink, setIsSavingLink] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [username, setUsername] = useState("");

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          navigate('/auth');
          return;
        }

        const { data, error } = await supabase
          .from('profiles')
          .select('service_page_link, company_name, profile_bio, profile_image_url, banner_image_url')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        // Cast data to our Profile type
        const profile = data as Profile;

        if (profile?.service_page_link) {
          // Extract username from service_page_link (handles both "services/username" and "username" formats)
          const extractedUsername = profile.service_page_link.includes('/') 
            ? profile.service_page_link.split('/').pop() 
            : profile.service_page_link;
          setUsername(extractedUsername || user.id);
          // Always construct as "services/{username}" for consistency
          setServiceLink(`services/${extractedUsername || user.id}`);
        } else {
          const link = generateServiceLink(user.id);
          setServiceLink(link);
          setUsername(user.id);
          await saveServiceLink(link, user.id);
        }
        
        if (profile?.company_name) {
          setBusinessName(profile.company_name);
        }
        
        if (profile?.profile_bio) {
          setProfileBio(profile.profile_bio);
        }
        
        if (profile?.profile_image_url) {
          setProfileImageUrl(profile.profile_image_url);
        }
        
        if (profile?.banner_image_url) {
          setBannerImageUrl(profile.banner_image_url);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        toast({
          title: "Error loading profile",
          description: "Could not load your service page link.",
          variant: "destructive",
        });
      }
    };

    fetchUserProfile();
  }, [navigate, toast]);

  useEffect(() => {
    if (serviceLink) {
      const fullUrl = getFullUrl();
      const url = generateQRCodeUrl(fullUrl);
      setQrCodeUrl(url);
    }
  }, [serviceLink]);

  const getFullUrl = () => {
    if (!serviceLink) return "";
    
    const baseUrl = window.location.origin;
    return `${baseUrl}/${serviceLink}`;
  };

  // Generate dynamic OG URL for social sharing
  const getDynamicOGUrl = () => {
    if (!username) return getFullUrl();
    
    // TEMPORARILY: Use direct service page URL until OG function auth is fixed
    return getFullUrl();
    
    // TODO: Re-enable when auth is fixed
    // const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    // return `${supabaseUrl}/functions/v1/dynamic-og?username=${encodeURIComponent(username)}`;
  };

  const saveServiceLink = async (link: string, userId: string) => {
    setIsSavingLink(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ service_page_link: link })
        .eq('id', userId);

      if (error) throw error;
      
      console.log("Service page link saved to profile:", link);
    } catch (err) {
      console.error('Error saving service link to profile:', err);
    } finally {
      setIsSavingLink(false);
    }
  };

  const shareOnSocial = (platform: string) => {
    const fullUrl = getFullUrl();
    // TEMPORARILY: Use direct URLs until OG function auth is fixed
    const shareUrl = fullUrl;
    
    // TODO: Re-enable when auth is fixed
    // const dynamicOGUrl = getDynamicOGUrl();
    let url = "";
    
    switch (platform) {
      case "instagram":
        // For Instagram, copy the regular URL since they don't support dynamic previews
        navigator.clipboard.writeText(fullUrl);
        window.open("https://www.instagram.com/accounts/edit", "_blank", "noopener,noreferrer");
        break;
      case "facebook":
        // TEMPORARILY: Use direct URL until OG function auth is fixed
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        break;
      case "whatsapp":
        // TEMPORARILY: Use direct URL until OG function auth is fixed
        url = `https://wa.me/?text=${encodeURIComponent(
          "Check out my services: " + shareUrl
        )}`;
        window.open(url, "_blank", "noopener,noreferrer");
        break;
    }
  };

  const handleDownloadStyledQR = async () => {
    try {
      if (!qrCodeUrl) {
        console.error("QR code URL is not set");
        toast({
          title: "QR Code not ready",
          description: "Please wait for the QR code to load and try again.",
          variant: "destructive",
        });
        return;
      }
      
      const fullUrl = getFullUrl();
      await createStyledQRCode(qrCodeUrl, businessName, fullUrl);
      toast({
        title: "Styled QR Code downloaded",
        description: "Your branded QR code has been downloaded successfully.",
      });
    } catch (err) {
      console.error('Error creating styled QR code:', err);
      toast({
        title: "Download failed",
        description: "There was a problem creating your styled QR code.",
        variant: "destructive",
      });
    }
  };

  const shareOnFacebook = () => {
    const shareUrl = getFullUrl();
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank');
  };

  const shareOnWhatsApp = () => {
    const shareUrl = getFullUrl();
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Check out these services: ${shareUrl}`)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareOnTelegram = () => {
    const shareUrl = getFullUrl();
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Check out these services!')}`;
    window.open(telegramUrl, '_blank');
  };

  return {
    navigate,
    businessName,
    profileBio,
    profileImageUrl,
    bannerImageUrl,
    qrCodeUrl,
    isSavingLink,
    getFullUrl,
    getDynamicOGUrl,
    shareOnSocial,
    handleDownloadStyledQR,
    shareOnFacebook,
    shareOnWhatsApp,
    shareOnTelegram
  };
};
